import React, { useEffect, useState } from 'react';
import { CheckIcon, ClipboardPlusIcon, MoreHorizontalIcon, PillIcon, Repeat2Icon, StethoscopeIcon, SyringeIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
type CheckInStep = 1 | 2 | 3 | 4;
type VisitPurpose = {
  label: string;
  Icon: typeof PillIcon;
};
type CheckInDialogProps = {
  open: boolean;
  onClose: () => void;
};
const visitPurposes: VisitPurpose[] = [{
  label: 'Refill pickup',
  Icon: PillIcon
}, {
  label: 'New prescription',
  Icon: ClipboardPlusIcon
}, {
  label: 'Transfer',
  Icon: Repeat2Icon
}, {
  label: 'Vaccine',
  Icon: SyringeIcon
}, {
  label: 'Consultation',
  Icon: StethoscopeIcon
}, {
  label: 'Other',
  Icon: MoreHorizontalIcon
}];
export function CheckInDialog({
  open,
  onClose
}: CheckInDialogProps) {
  const [step, setStep] = useState<CheckInStep>(1);
  const [visitPurpose, setVisitPurpose] = useState('');
  const [wait, setWait] = useState('');
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!open) {
      setStep(1);
      setVisitPurpose('');
      setWait('');
    }
  }, [open]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open, onClose]);
  const title = step === 1 ? 'What was your visit for today?' : step === 2 ? 'How long is the wait?' : step === 3 ? 'How did it feel today?' : 'You made a difference.';
  return <AnimatePresence data-id="element-11" data-mp="22u66x120i8sz">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end bg-black/45 p-0 sm:items-center sm:justify-center sm:p-6" initial={reduceMotion ? false : {
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-12" data-mp="22t4twr1frc8m">
          <motion.section role="dialog" aria-modal="true" aria-labelledby="checkin-title" className="w-full bg-[#F7F7F7] p-4 shadow-2xl sm:max-w-xl sm:rounded-lg sm:p-6" initial={reduceMotion ? false : {
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-13" data-mp="3f50e6ut9ds05">
            <div className="rounded-lg border border-[#E1E1DC] bg-white p-5 sm:p-7" data-id="element-14" data-mp="ggtin412htz1">
              <div className="flex items-start justify-between gap-6" data-id="element-15" data-mp="lemjrinijxsg">
                <div data-id="element-16" data-mp="25yhz15az1eu4">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#A63A2B]" data-id="element-17" data-mp="25xp02qi97tmj">
                    Quick community check-in
                  </p>
                  <h2 id="checkin-title" className="mt-2 text-2xl font-black tracking-tight text-[#1C1C1C] sm:text-3xl" data-id="element-18" data-mp="1w7vnpnaafah8">
                    {title}
                  </h2>
                </div>
                <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center text-[#666] hover:bg-[#F7F7F7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" aria-label="Close check-in" data-id="element-19" data-mp="1mbkbexap6kms">
                  <XIcon className="h-5 w-5" data-id="element-20" data-mp="37nbzqsazbe9h" />
                </button>
              </div>

              {step === 1 && <div className="mt-6" data-id="element-21" data-mp="32o7nfssrd0ex">
                  <p className="max-w-md text-sm leading-6 text-[#686868] sm:text-base" data-id="element-22" data-mp="32paj0e10zr16">
                    Your experience helps the next person know before they go.
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-23" data-mp="24bvfi1xhtybu">
                    {visitPurposes.map(({
                label,
                Icon
              }) => <button key={label} type="button" onClick={() => {
                setVisitPurpose(label);
                setStep(2);
              }} className="flex min-h-[100px] flex-col items-start justify-center gap-3 rounded-lg border border-[#DEDCD6] bg-[#FCFBF8] p-4 text-left text-sm font-black text-[#1C1C1C] hover:border-[#008F82] hover:bg-[#F4F8F5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008F82] sm:min-h-[112px] sm:flex-row sm:items-center sm:justify-start sm:gap-3 sm:text-base" data-id="element-24" data-mp="1vbzyvhl65b4f">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#EEF5F0] text-[#008F82]" data-id="element-25" data-mp="12pf8ajcip31x">
                          <Icon className="h-5 w-5" aria-hidden="true" data-id="element-26" data-mp="ezxkmn4dqu9x" />
                        </span>
                        <span data-id="element-27" data-mp="szfc5a928lmz">{label}</span>
                      </button>)}
                  </div>
                </div>}

              {step === 2 && <div className="mt-7" data-id="element-28" data-mp="120dhpwz5d17o">
                  <p className="text-sm leading-6 text-[#5C5C5C]" data-id="element-29" data-mp="11zbuku4uwo9l">
                    Thanks for sharing {visitPurpose.toLowerCase()}. Choose the
                    closest estimate.
                  </p>
                  <div className="mt-5 grid grid-cols-2 gap-3" data-id="element-30" data-mp="1bovdfzskki4g">
                    {['<5 mins', '5–10 mins', '10–20 mins', '20+ mins'].map(item => <button key={item} type="button" onClick={() => {
                setWait(item);
                setStep(3);
              }} className="rounded-sm border border-[#DCDCDC] px-4 py-4 text-sm font-bold text-[#1C1C1C] hover:border-[#A63A2B] hover:bg-[#FCF5F3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-31" data-mp="1lnkxnz7xuenv">
                          {item}
                        </button>)}
                  </div>
                  <BackButton onClick={() => setStep(1)} label="Back to visit type" data-id="element-32" data-mp="27ijmubktt0mc" />
                </div>}

              {step === 3 && <div className="mt-7" data-id="element-33" data-mp="2lg83t8brvnm4">
                  <p className="text-sm leading-6 text-[#5C5C5C]" data-id="element-34" data-mp="2lf6up9ebhb71">
                    You logged{' '}
                    <strong className="text-[#1C1C1C]" data-id="element-35" data-mp="m45ma3mpl3b3">{wait}</strong>. Was your
                    medication ready?
                  </p>
                  <div className="mt-5 grid gap-3" data-id="element-36" data-mp="r4bbld5iadq0">
                    {['Yes, ready when I arrived', 'Almost ready — small delay', 'Not ready yet'].map(item => <button key={item} type="button" onClick={() => setStep(4)} className="rounded-sm border border-[#DCDCDC] px-4 py-3 text-left text-sm font-bold text-[#1C1C1C] hover:border-[#A63A2B] hover:bg-[#FCF5F3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-37" data-mp="9txtv31txsf3">
                        {item}
                      </button>)}
                  </div>
                  <BackButton onClick={() => setStep(2)} label="Back to wait time" data-id="element-38" data-mp="nk1exqoz3g2y" />
                </div>}

              {step === 4 && <div className="mt-7" data-id="element-39" data-mp="38cv29da6cjs4">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-[#EEF6F0] text-[#008F82]" data-id="element-40" data-mp="1t09la37b5vr">
                    <CheckIcon className="h-6 w-6" data-id="element-41" data-mp="6te5xuvfwrdy" />
                  </div>
                  <p className="mt-4 text-lg font-black text-[#1C1C1C]" data-id="element-42" data-mp="3oyhg3u09jtl8">
                    Status logged! You just saved 14 people time today.
                  </p>
                  <p className="mt-2 text-sm leading-6 text-[#5C5C5C]" data-id="element-43" data-mp="3t1e4y2geclxm">
                    Want to keep a record of the pharmacies that work for you?
                  </p>
                  <button type="button" onClick={onClose} className="mt-6 w-full rounded-sm bg-[#1C1C1C] px-4 py-3 text-sm font-bold text-white hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1C1C1C] focus-visible:ring-offset-2" data-id="element-44" data-mp="picie8um9bxj">
                    Continue with Google
                  </button>
                  <button type="button" onClick={onClose} className="mt-3 w-full px-4 py-2 text-sm font-bold text-[#666] hover:text-[#1C1C1C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-45" data-mp="18an62vgp93b2">
                    Not now
                  </button>
                  <BackButton onClick={() => setStep(3)} label="Back to medication question" data-id="element-46" data-mp="2xq7kwlg6g191" />
                </div>}
            </div>
            <p className="px-2 pt-4 text-center text-xs leading-5 text-[#777]" data-id="element-47" data-mp="252v8v57n7btz">
              Free · takes under a minute · no account needed until you submit
            </p>
          </motion.section>
        </motion.div>}
    </AnimatePresence>;
}
function BackButton({
  onClick,
  label
}: {
  onClick: () => void;
  label: string;
}) {
  return <button type="button" onClick={onClick} className="mt-5 text-sm font-bold text-[#666] underline underline-offset-4 hover:text-[#1C1C1C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A63A2B]" data-id="element-48" data-mp="3vg2f4jet1vuu">
      {label}
    </button>;
}